import { FC, isValidElement, ReactNode } from 'react';
import { Box, styled } from '@mui/material';

export interface RideHailingServiceChipsProps {
	label: ReactNode;
	icon?: ReactNode;
	testId?: string;
	isWalkChip?: boolean;
}

const ChipContainer = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isWalkChip',
})<{ isWalkChip?: boolean }>(({ theme, isWalkChip }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: 'auto',
	padding: '4px 6px',
	borderRadius: '80px',
	border: isWalkChip ? 'none' : `0.25px solid ${theme.palette.primary[200]}`, // #ABB7ED
	backgroundColor: isWalkChip
		? theme.palette.solutions?.[100] ?? '#E9EDF5'
		: theme.palette.neutral.main,
	color: theme.palette.primary.main,
	fontFamily: '"Public Sans", sans-serif',
	fontSize: '10px',
	fontStyle: 'normal',
	fontWeight: 400,
	lineHeight: '150%',
	gap: '2px',
	whiteSpace: 'normal',
	wordBreak: 'break-word',
	'& svg': {
		flexShrink: 0,
	},
}));

const IconWrapper = styled(Box)(() => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
}));

const LabelText = styled(Box)(() => ({
	display: 'flex',
	alignItems: 'center',
	lineHeight: '150%',
}));

const isWalkIcon = (icon: ReactNode): boolean =>
	isValidElement(icon) && (icon.props as { mode?: string })?.mode === 'WALK';

export const RideHailingServiceChips: FC<RideHailingServiceChipsProps> = ({
	label,
	icon,
	testId,
	isWalkChip,
}) => {
	const computedIsWalkChip = isWalkChip ?? (icon ? isWalkIcon(icon) : false);

	return (
		<ChipContainer data-testid={testId} isWalkChip={computedIsWalkChip}>
			{icon && <IconWrapper>{icon}</IconWrapper>}
			<LabelText>{label}</LabelText>
		</ChipContainer>
	);
};
